import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  AddSqToKbRequest,
  Answer,
  AnswerAgainRequest,
  AnswerAgainResponse,
  CreateOrUpdateAnswerRequest,
  DeleteQuestionnaireRequest,
  GetAccountBalanceResponse,
  GetAnswerSourcesRequest,
  GetAnswerSourcesResponse,
  GetQuestionnaireRequest,
  GetQuestionnairesResponse,
  Question,
  Questionnaire,
  QUESTIONNAIRE_ANSWER_TYPE,
  QUESTIONNAIRE_LEVEL_OF_DETAIL,
  QUESTIONNAIRE_TYPE,
  UploadKnowledgeBaseRequest,
  UploadQuestionnaireRequest,
  UploadQuestionnaireResponse,
} from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import {
  ChatCitation,
  ChatContextSourceType,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { QuestionnaireServicePromiseClient } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useGrpcRevalidateByMethod } from 'src/components'
import log from 'loglevel'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'
import { useThrobberContext } from 'src/Throbber'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClientWithContentUpdate } from 'src/adapter/grpcClient'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  BuyerOrigin,
  SendWelcomeEmailRequest,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { getCreateChatContextRequest } from 'src/components/Reusable/ChatContext/chatContext.utils'
import { ChatContext } from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import {
  generatePermalinkFromId,
  useCompanyInfoPermalink,
} from 'src/components/PageLayout/Permalink/generatePermalink'
import {
  QUESTIONNAIRE_QUESTION_PARAM,
  QUESTIONNAIRE_SECTION_PARAM,
} from 'src/components/Reusable/Questionnaire/questionnaireSidebarTab.hooks'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { runAuditBotAnimation } from '../AuditBot/AuditBot.helpers'
import { getCompletedQuestions } from './securityQuestionnaire.helpers'
import { getQuestionnaireAsFile } from './modals/securityQuestionnaireModals.helpers'

type QuestionsType = {
  question: string
  answer?: string
}

type UploadQuestionnaireProps = {
  questions: QuestionsType[]
  name?: string
  org?: string
  dueDate?: Timestamp
  answerType: QUESTIONNAIRE_ANSWER_TYPE
  chatContext?: ChatContext
  isEdit?: boolean
  levelOfDetail?: QUESTIONNAIRE_LEVEL_OF_DETAIL
}

export const useUploadSecurityQuestionnaire = (
  hideThrobber?: boolean,
): (({
  questions,
  name,
  org,
  dueDate,
  answerType,
  chatContext,
  isEdit,
  levelOfDetail,
}: UploadQuestionnaireProps) => Promise<
  UploadQuestionnaireResponse | undefined
>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )
  const { setThrobberState } = useThrobberContext()
  const mutate = useInvalidateQuestionnaireCache()

  const uploadQuestionnaire = useCallback(
    async ({
      questions,
      name,
      org,
      dueDate,
      answerType,
      chatContext,
      levelOfDetail,
    }: UploadQuestionnaireProps) => {
      const requestQuestions = questions.map((elem) =>
        new Question().setAnswer(elem.answer || '').setQuestion(elem.question),
      )

      const request = new UploadQuestionnaireRequest()
      request.setName(name || '')
      request.setOrganization(org || '')
      request.setQuestionsList(requestQuestions)
      request.setDueDate(dueDate)
      request.setQuestionnaireAnswerType(answerType)
      request.setLevelOfDetail(
        levelOfDetail || QUESTIONNAIRE_LEVEL_OF_DETAIL.HIGH,
      )

      if (chatContext) {
        request.setChatContext(getCreateChatContextRequest(chatContext))
      }

      const res = await questionnaireClient.uploadQuestionnaire(request)
      await mutate()
      return res
    },
    [questionnaireClient, mutate],
  )

  const fireSecurityQuestionnaire = useCallback(
    async ({
      questions,
      name,
      org,
      dueDate,
      answerType,
      chatContext,
      isEdit,
      levelOfDetail,
    }: UploadQuestionnaireProps) => {
      !hideThrobber &&
        runAuditBotAnimation(
          setThrobberState,
          isEdit
            ? ['Scanning Initiated', 'Each question takes about a minute.']
            : 'Initiated AI GRC Q&A. This may take a few minutes.',
        )

      return await uploadQuestionnaire({
        questions,
        name,
        org,
        dueDate,
        answerType,
        chatContext,
        levelOfDetail,
      })
    },
    [setThrobberState, hideThrobber, uploadQuestionnaire],
  )

  return fireSecurityQuestionnaire
}

export const useUpdateAnswer = (): ((answer: Answer) => Promise<void>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  return async (answer: Answer) => {
    const req = new CreateOrUpdateAnswerRequest().setAnswer(answer)
    await questionnaireClient.createOrUpdateAnswer(req)
  }
}

export const useAddToKnowledgeBase = (): ((
  questionnaire: Questionnaire,
) => Promise<void>) => {
  const mutate = useInvalidateQuestionnaireCache()
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  return async (questionnaire) => {
    try {
      const acceptedQuestions = getCompletedQuestions(
        questionnaire.getQuestionsList(),
      )
      if (!acceptedQuestions.length) {
        showInfoToast(
          'Accept at least one answer before adding this AI GRC Q&A result to your knowledge base.',
        )
        return
      }

      const file = await getQuestionnaireAsFile(acceptedQuestions)
      if (!file.length) {
        showInfoToast(
          'Unable to add this AI GRC Q&A result to your knowledge base because the generated file is empty.',
        )
        log.error(
          'empty AI GRC Q&A knowledge base file generated for questionnaire id:',
          questionnaire.getId(),
        )
        return
      }

      const request = new AddSqToKbRequest()
        .setKnowledgeBase(
          new UploadKnowledgeBaseRequest()
            .setFile(file)
            .setFileName(questionnaire.getName())
            .setQuestionCount(acceptedQuestions.length)
            .setAnswerCount(acceptedQuestions.length),
        )
        .setSecurityQuestionnaireId(questionnaire.getId())
      await questionnaireClient.addQuestionnaireToKnowledgeBase(request)
      mutate()
    } catch (err) {
      log.error(
        'error when attempting to add AI GRC Q&A result to knowledge base id:',
        questionnaire.getId(),
      )
    }
  }
}

export const useUpdateAnswerFromQuestion = (): ((
  question: Question,
) => Promise<void>) => {
  const updateFunc = useUpdateAnswer()

  return async (question: Question) => {
    const answer = new Answer()
    answer
      .setId(question.getAnswerId())
      .setAnswer(question.getAnswer())
      .setAcceptance(question.getAcceptance())
      .setStatus(question.getStatus())
      .setQuestionId(question.getId())

    await updateFunc(answer)
  }
}

export const useGetQuestionnaires =
  (): GrpcResponse<GetQuestionnairesResponse> => {
    const { response } = useSwrImmutableGrpc(
      QuestionnaireServicePromiseClient.prototype.getQuestionnaires,
      new Empty(),
    )

    return NewGrpcResponse(response)
  }

export const useGetQuestionnaire = ({
  id,
  allowFetch = true,
  includeDeleted = false,
}: {
  id?: string
  allowFetch?: boolean
  includeDeleted?: boolean
}): GrpcResponse<Questionnaire> => {
  const shouldFetch = !!(id && id.length) && allowFetch
  const request = new GetQuestionnaireRequest().setId(id || '')
  includeDeleted &&
    request.setIncludeDeleted(new BoolValue().setValue(includeDeleted))
  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getQuestionnaire,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export const useDeleteQuestionnaire = (): ((id: string) => Promise<void>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  return async (id: string) => {
    const req = new DeleteQuestionnaireRequest().setId(id)
    await questionnaireClient.deleteQuestionnaire(req)
  }
}

export const useAnswerSources = (
  questionId: string,
  questionnaireType?: QUESTIONNAIRE_TYPE,
  shouldFetch = false,
): GrpcResponse<GetAnswerSourcesResponse> => {
  const request = new GetAnswerSourcesRequest().setQuestionId(questionId)
  questionnaireType && request.setType(questionnaireType)

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getAnswerSources,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export const useUpdateQuestionnaire = (): ((
  questionnaire: Questionnaire,
) => Promise<void>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )
  const mutate = useInvalidateQuestionnaireCache()

  return useCallback(
    async (questionnaire: Questionnaire) => {
      await questionnaireClient.updateQuestionnaire(questionnaire)
      await mutate()
    },
    [questionnaireClient, mutate],
  )
}

export const useAnswerQuestionAgain = (
  question: Question,
): (() => Promise<AnswerAgainResponse | void>) => {
  // we will refetch the AI GRC Q&A result after starting the task and will get the loading state for free
  const { mutate, data } = useGetQuestionnaire({
    id: question.getQuestionnaireId(),
  })
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  const answerQuestionAgain =
    useCallback(async (): Promise<AnswerAgainResponse> => {
      const request = new AnswerAgainRequest().setQuestion(question)
      const response = await questionnaireClient.answerQuestionAgain(request)
      await mutate()
      return response
    }, [questionnaireClient, mutate, question])

  return useCallback(async (): Promise<AnswerAgainResponse | void> => {
    if (!data) {
      return
    }
    return await answerQuestionAgain()
  }, [answerQuestionAgain, data])
}

// to *slightly* improve our cache invalidation strategy
// we will determine what route the user is on to invalidate
// the correct RPC
export const useInvalidateQuestionnaireCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    const request = window.location.href.includes('index')
      ? QuestionnaireServicePromiseClient.prototype.getQuestionnaires
      : QuestionnaireServicePromiseClient.prototype.getQuestionnaire

    try {
      await mutateFunc(request)
    } catch (err) {
      log.error('Error when invalidating AI GRC Q&A cache', err)
    }
  }, [mutateFunc])
}

export const useSecurityQuestionnaireBalance =
  (): GrpcResponse<GetAccountBalanceResponse> => {
    const { response } = useSwrGrpc(
      QuestionnaireServicePromiseClient.prototype.getAccountBalance,
      new Empty(),
    )

    return NewGrpcResponse(response)
  }

export const useSendWelcomeEmail = (): ((email: string) => Promise<void>) => {
  const AccountClient =
    useAuthorizedGrpcClientWithContentUpdate(AccountPromiseClient)
  return async (email: string) => {
    try {
      const request = new SendWelcomeEmailRequest()
        .setEmail(email)
        .setBuyerOrigin(BuyerOrigin.SECURITY_QUESTIONNAIRE)
      await AccountClient.sendSaasBuyerWelcomeEmail(request)
    } catch (e) {
      log.error(`Error in AI GRC Q&A hooks send welcome email ${email}`, e)
    }
  }
}

export type AnswerQuestionnaireAgainResponse = {
  isLoading: boolean
  error: string | null
  mutate: (
    questionnaireId: string,
    chatContext: ChatContext,
  ) => Promise<UploadQuestionnaireResponse | undefined>
}

export const useCitationLink = (citation: ChatCitation): string => {
  const params = useParams()
  const companyLink = useCompanyInfoPermalink()

  const modelType = useMemo(() => {
    const sourceType = citation.getType()
    switch (sourceType) {
      case ChatContextSourceType.CONTROL:
        return MODEL_TYPE.CONTROL
      case ChatContextSourceType.POLICY:
        return MODEL_TYPE.POLICY
      case ChatContextSourceType.EVIDENCE:
        return MODEL_TYPE.EVIDENCE
      case ChatContextSourceType.USERS:
        return MODEL_TYPE.USEROBJ
      case ChatContextSourceType.AUDIT:
        return MODEL_TYPE.AUDIT
      case ChatContextSourceType.DMS:
        return MODEL_TYPE.DOCUMENT_MANAGEMENT
      case ChatContextSourceType.RECEPTORS:
        return MODEL_TYPE.RECEPTOROBJ
      case ChatContextSourceType.VENDORS:
        return MODEL_TYPE.VENDOR
      case ChatContextSourceType.RISKS:
        return MODEL_TYPE.RISK
      case ChatContextSourceType.EXAMINATION_AND_TEST:
        return MODEL_TYPE.AUDITBOT_RUN
      case ChatContextSourceType.CONTROL_LIST:
        return MODEL_TYPE.CONTROL
      case ChatContextSourceType.POLICY_LIST:
        return MODEL_TYPE.POLICY
      case ChatContextSourceType.REQUESTS:
        return MODEL_TYPE.REQUEST
      default:
        return null
    }
  }, [citation])

  return useMemo(() => {
    // SUPPORT_KNOWLEDGE: doc-group hits have a contentId and open in the viewer;
    // web hits store an external URL in the content field.
    if (citation.getType() === ChatContextSourceType.SUPPORT_KNOWLEDGE) {
      if (citation.getContentId()) return ''
      return citation.getContent()
    }
    if (citation.getType() === ChatContextSourceType.COMPANY) {
      return companyLink
    }
    // A prior answer opens the questionnaire it was given on, at that
    // question. Without a location (no longer indexed) there is nowhere to go.
    if (citation.getType() === ChatContextSourceType.INBOUND_QUESTIONNAIRE) {
      const priorAnswer = citation.getPriorAnswer()
      if (!priorAnswer) return ''
      return generatePermalinkFromId({
        pageContext: params.pageContext as string,
        modelType: MODEL_TYPE.VENDOR_QUESTIONNAIRE,
        modelId: priorAnswer.getQuestionnaireId(),
        isInternalLink: false,
        includeSearchParams: true,
        search: {
          [QUESTIONNAIRE_SECTION_PARAM]: String(priorAnswer.getSectionNumber()),
          [QUESTIONNAIRE_QUESTION_PARAM]: String(
            priorAnswer.getQuestionNumber(),
          ),
        },
      })
    }
    if (modelType) {
      const search: Record<string, string> = {}
      if (citation.getPageNumber() > 0) {
        search.citation_page = citation.getPageNumber().toString()
      }
      if (citation.getContentId()) {
        search.citation_content_id = citation.getContentId()
      }
      return generatePermalinkFromId({
        pageContext: params.pageContext as string,
        modelType,
        modelId: citation.getReferenceId(),
        isInternalLink: false,
        includeSearchParams:
          citation.getPageNumber() > 0 || !!citation.getContentId(),
        search: Object.keys(search).length > 0 ? search : undefined,
      })
    } else {
      return ''
    }
  }, [params, citation, modelType, companyLink])
}
